/**
 * @prettier
 */

import React, { useCallback, useEffect, useState } from 'react';
import cx from 'classnames';
import ProductColorPicker from 'cards/components/common/ProductColorPicker/ProductColorPicker';
import MinimalLoader from 'components/common/ui/MinimalLoader';
import { lookupSelectedVariation } from '../util/helpers';
import PreviewPages from './PreviewPages';
import Canvas from './Canvas';
import styles from './styles.module.less';

// We need to import this directly to download the custom fonts when referenced in the preview.
import 'cards/stylesheets/_fonts.less';

const SamplePreview = ({
  hideColorPicker,
  colors,
  form,
  template,
  updateForm,
  sampleNotCustomizable,
  skuData,
  isMagnet,
  cardSuiteDetails,
  isMulti,
  enableHoverActions,
  disablePagination,
  setMultiPreviewState,
  multiPreviewState,
  cardSuiteUUIDList,
  cardSuiteDetailsSet,
  width,
}) => {
  const [cache, setCache] = useState({
    preview: multiPreviewState.activePreview,
    template,
  });

  useEffect(() => {
    if (!cache.template || (template && template.id !== cache.template.id)) {
      setCache({
        preview: multiPreviewState.activePreview,
        template,
      });
    }
  }, [template, cache.template, multiPreviewState.activePreview]);

  const page = cache.template.pages[0];

  const previewBoxStyle = { width: width + 20, height: width + 20 };

  const previewLoading = cache.preview !== multiPreviewState.activePreview;

  const activePreview = cache.preview;

  let { variation } = form.customizations;
  if (isMulti) {
    variation = form.customizations.variationSet[activePreview];
  }

  const activeColor = isMulti
    ? form.customizations.colorSet[activePreview]
    : form.customizations.color;

  const isRounded = variation && variation.option_values.silhouette === 'rounded';

  const setColor = useCallback(
    color => {
      if (!isMulti) {
        const selectedOptions = { ...variation.option_values, color: color.value };
        const newVariation = lookupSelectedVariation(selectedOptions, cardSuiteDetails, isMagnet);
        updateForm({ customizations: { color: color.value, variation: newVariation } });
      }

      if (isMulti && activePreview) {
        const selectedOptions = { ...variation.option_values, color: color.value };
        const newVariation = lookupSelectedVariation(
          selectedOptions,
          cardSuiteDetailsSet[activePreview],
          isMagnet
        );

        updateForm({
          customizations: {
            colorSet: { [activePreview]: color.value },
            variationSet: { [activePreview]: newVariation },
          },
        });
      }
    },
    [
      activePreview,
      cardSuiteDetails,
      cardSuiteDetailsSet,
      isMagnet,
      isMulti,
      updateForm,
      variation.option_values,
    ]
  );

  const activePreviewIndex = (cardSuiteUUIDList || []).findIndex(id => id === activePreview);

  const nextPreview = (cardSuiteUUIDList || [])[activePreviewIndex + 1];

  const lastPreview = (cardSuiteUUIDList || [])[activePreviewIndex - 1];

  const previewPagination = isMulti && !disablePagination && (
    <>
      <PreviewPages.PageButton
        last
        disabled={!lastPreview}
        onClick={() => setMultiPreviewState({ ...multiPreviewState, activePreview: lastPreview })}
      />
      <PreviewPages.PageButton
        next
        disabled={!nextPreview}
        onClick={() => setMultiPreviewState({ ...multiPreviewState, activePreview: nextPreview })}
      />
    </>
  );

  if (previewLoading) {
    return (
      <div className={styles.box}>
        <div className={styles.centeredBox}>
          <div className={styles.previewBox} style={previewBoxStyle}>
            <MinimalLoader />
            {previewPagination}
          </div>
          <div className={cx(styles.colorPickerBox, { [styles.invisible]: hideColorPicker })} />
        </div>
      </div>
    );
  }

  if (sampleNotCustomizable && skuData && skuData.image) {
    return (
      <div className={styles.box}>
        <div className={styles.centeredBox}>
          <div className={styles.previewBox} style={previewBoxStyle}>
            <img
              key={skuData.image}
              className={styles.nonEditableImg}
              src={skuData.image}
              alt="sample preview"
            />
            {previewPagination}
          </div>
          <div className={cx(styles.colorPickerBox, { [styles.invisible]: hideColorPicker })} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.box}>
      <div className={styles.centeredBox}>
        <div className={styles.previewBox} style={previewBoxStyle}>
          {cache.template && (
            <>
              <Canvas
                isMulti={isMulti}
                updateForm={updateForm}
                page={page}
                isRounded={isRounded}
                customizations={form && form.customizations}
                cardSuiteUUIDList={cardSuiteUUIDList}
                activePreview={activePreview}
                multiPreviewState={multiPreviewState}
                setMultiPreviewState={setMultiPreviewState}
                enableHoverActions={enableHoverActions}
                width={width}
              />
              {previewPagination}
            </>
          )}
        </div>
        <div className={cx(styles.colorPickerBox, { [styles.invisible]: hideColorPicker })}>
          {!hideColorPicker && (
            <ProductColorPicker
              colors={colors}
              activeColor={activeColor}
              maxColorsToShow={50}
              onSelect={setColor}
            />
          )}
        </div>
      </div>
    </div>
  );
};

SamplePreview.defaultProps = {
  width: 280,
};

export default SamplePreview;
